// History service DTOs (mirrored from Java DTOs)

import type { OutcomeStatus } from '@/lib/api/event/models';
import type { PlayerLookDto } from '@/lib/api/rewards-hub/types';

export type OrderSide = 'BUY' | 'SELL';
export type OrderType = 'MARKET' | 'LIMIT';
export type OrderStatus =
  | 'PENDING'
  | 'FILLED'
  | 'PARTIALLY_FILLED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'EXECUTED'
  | 'ACTIVE';

// OrderHistoryDto from backend (short field names)
export interface OrderHistoryDto {
  i: string; // id (UUID)
  u: string; // userId (UUID)
  e: string; // eventId (UUID)
  o: string; // eventOutcomeId (UUID)
  si: OrderSide; // side
  t: OrderType; // type
  p: number; // price (BigDecimal)
  m: number; // placementTime (long timestamp)
  st: OrderStatus; // status
  q: number; // quantity (int)
  oq?: number; // originalQuantity (int) - optional, may be present in active orders
  mq?: number; // matchedQuantity (int) - optional, may be present in active orders
  ex?: string; // expireAt (ISO datetime string "yyyy-MM-dd'T'HH:mm:ss.SSS") - optional
}

// TradeHistoryDto from backend (short field names)
export interface TradeHistoryDto {
  i: string; // id (UUID)
  b: string; // buyerId (UUID)
  s: string; // sellerId (UUID)
  c: string; // contractId (UUID)
  d: string; // dealId (UUID)
  e: string; // eventId (UUID)
  o: string; // eventOutcomeId (UUID)
  a: string; // assetId (UUID) - resolution ID (YES or NO token)
  q: number; // quantity (int)
  p: number; // price (BigDecimal)
  f: number; // fee (BigDecimal)
  r: number; // revenue (BigDecimal)
  m: string; // matchTime (ISO datetime string)
}

// Pageable query parameters
export interface PageableQuery {
  page?: number;
  size?: number;
  sort?: string[];
}

// Filters query parameters (generic object)
export interface FiltersQuery {
  [key: string]: string | number | boolean | string[] | undefined;
}

// Paginated response structure
export interface PageResponseDto<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
  sort?: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  pageable?: {
    offset: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    paged: boolean;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
  };
}

// ActiveOrderDto from backend (for /history/orders endpoint with eventOutcomeId)
export interface ActiveOrderDto {
  i: string; // id (UUID)
  u: string; // userId (UUID)
  e: string; // eventId (UUID)
  o: string; // eventOutcomeId (UUID)
  a: string; // assetId (UUID) - resolution ID (YES or NO token)
  oq: number; // originalQuantity (int)
  si: OrderSide; // side
  t: OrderType; // type
  p: number; // price (BigDecimal)
  m: number; // matched quantity (int)
  st: string; // status (ACTIVE, FILLED, CANCELED, etc.)
  q: number; // quantity (int)
  ex?: string; // expireAt (ISO datetime string "yyyy-MM-dd'T'HH:mm:ss.SSS") - optional
}

// ActiveOrderPageResponseDto - response structure for /history/orders
export interface ActiveOrderPageResponseDto {
  i: ActiveOrderDto[]; // items array
  p: number; // page number
  s: number; // size
  tp: number; // totalPages
}

// TradeHistoryPageResponseDto - response structure for /history/trades
// Uses compact format similar to ActiveOrderPageResponseDto
export interface TradeHistoryPageResponseDto {
  i: TradeHistoryDto[]; // items array
  p: number; // page number
  s: number; // size
  tp: number; // totalPages
}

export type OrderHistoryPageResponseDto = PageResponseDto<OrderHistoryDto>;

// ActivityItemDto from backend (short field names)
export interface ActivityItemDto {
  tid: string; // transactionId (UUID)
  u: string; // userId (UUID)
  n: string; // username
  oi: string; // outcomeId (UUID)
  on: string; // outcomeName
  p: string; // position
  si: string; // side (BUY/SELL)
  q: number; // shares (BigDecimal)
  x: string; // executedAt (ISO datetime string)
  lk?: PlayerLookDto | null; // Rewards Hub look
}

// ActivityPageResponseDto - response structure for /history/activity/by-event/{eventId}
export interface ActivityPageResponseDto {
  i: ActivityItemDto[]; // list/items array
  p: number; // page number
  s: number; // size
  t: number; // totalPages
}

// FullActivityItemDto from backend (short field names) - for /history/activity endpoint
export interface FullActivityItemDto {
  u: string; // userId (UUID)
  n: string; // username
  oi: string; // outcomeId (UUID)
  on: string; // outcomeName
  p: string; // position
  si: string; // side (BUY/SELL)
  q: number; // shares (BigDecimal)
  x: string; // executedAt (ISO datetime string)
  eq: string; // eventQuestion
  ei: string; // eventIcon
  es: string; // eventSlug
  tn: string; // tokenName
  ip: boolean; // isPrimary
  pr: number; // price (BigDecimal)
  ev: number; // eventVolume (BigDecimal)
}

// FullActivityListResponseDto - response structure for /history/activity
export interface FullActivityListResponseDto {
  i: FullActivityItemDto[]; // items array
  p: number; // page number
  s: number; // size
  t: number; // totalCount (approximate total number of activity records)
}

// FullOrderHistoryDto from backend (short field names) - for /api/history/orders/full and /api/history/orders/all
export interface FullOrderHistoryDto {
  i: string; // id (UUID)
  u: string; // userId (UUID)
  e: string; // eventId (UUID)
  o: string; // outcomeId (UUID)
  a: string; // assetId (UUID)
  oq: number; // originalQuantity
  si: OrderSide; // side (BUY/SELL)
  t: OrderType; // type (MARKET/LIMIT)
  p: number; // price
  m: number; // createdAt (timestamp in milliseconds)
  ex?: string; // executionTime (ISO datetime string)
  st: OrderStatus; // status (ACTIVE, EXECUTED, PARTIALLY_EXECUTED, CANCELLED, FAILED)
  q: number; // filledQuantity
  eq: string; // eventQuestion
  ei: string; // eventIcon
  es: string; // eventSlug
  on: string; // outcomeName
  tn: string; // tokenName
  ip: boolean; // isPrimary
}

// FullOrderHistoryListResponseDto - response structure for /api/history/orders/full and /api/history/orders/all
export interface FullOrderHistoryListResponseDto {
  i: FullOrderHistoryDto[]; // items array
  p: number; // page number
  s: number; // size
  tp: number; // totalPages
}

// TradeHistoryFullDto from backend (short field names) - for /api/history/trades
export interface TradeHistoryFullDto {
  i: string; // id (UUID)
  u: string; // userId (UUID)
  e: string; // eventId (UUID)
  o: string; // outcomeId (UUID)
  a: string; // assetId (UUID)
  si: OrderSide; // side (BUY/SELL)
  q: number; // quantity
  p: number; // price
  m: string; // executionTime (ISO datetime string)
  eq: string; // eventQuestion
  ei: string; // eventIcon
  es: string; // eventSlug
  on: string; // outcomeName
  tn: string; // tokenName
  ip: boolean; // isPrimary
  v: number; // value (quantity * price)
}

// TradeHistoryFullListResponseDto - response structure for /api/history/trades
export interface TradeHistoryFullListResponseDto {
  i: TradeHistoryFullDto[]; // items array
  p: number; // page number
  s: number; // size
  tp: number; // totalPages
}

// LastTradeStatsItemDto from /history/stats/last-trades (public, no auth)
export interface LastTradeStatsItemDto {
  u: string; // userId
  tra: string; // tradeId
  n: string; // username
  oi: string; // outcomeId
  on: string; // outcomeName
  p: string; // position
  si: string; // side (BUY/SELL)
  q: number; // shares
  x: string; // executedAt (ISO datetime)
  eq: string; // eventQuestion
  ei: string; // eventIcon
  es: string; // eventSlug
  tn: string; // tokenName
  ip: boolean; // isPrimary
  pr: number; // price
  ev: number; // eventVolume
  uv: number; // userVolume
  lk?: PlayerLookDto | null; // Rewards Hub look
}

// Paginated response: { i: items[], p: page, s: size, t: totalCount }
export interface LastTradeStatsResponseDto {
  i: LastTradeStatsItemDto[];
  p: number;
  s: number;
  t: number;
}

// ===== Deals Controller Types (/api/history/deals/*) =====

export interface TpSlItem {
  tp: number | null; // takeProfitPrice
  sl: number | null; // stopLossPrice
  q: number; // quantity (shares)
  i: string; // orderId
}

export type DatePreset =
  | 'TODAY'
  | 'YESTERDAY'
  | 'LAST_WEEK'
  | 'LAST_MONTH'
  | 'LAST_3_MONTHS'
  | 'LAST_YEAR';

export type PnlSign = 'POSITIVE' | 'NEGATIVE';

// --- Raw DTO response wrapper (short field names from backend) ---

export interface DealsRawPageResponse<T> {
  i: T[]; // items
  p: number; // page
  s: number; // size
  tp: number; // totalPages
  ti: number; // totalItems
}

// --- Raw DTOs (short field names matching @JsonProperty) ---

export interface OpenPositionRawDto {
  oi: string; // orderId
  ei: string; // eventId
  eoi: string; // eventOutcomeId
  ai: string; // assetId
  et: string; // eventTitle
  eic: string; // eventIcon
  es: string; // eventSlug
  eim: string; // eventImage
  on: string; // outcomeName
  tn: string; // tokenName
  ip: boolean; // isPrimary
  tsc: number; // totalSharesCount
  asc: number; // availableSharesCount
  sp: number; // sharePrice
  c: number; // cost
  cp: number; // currentPrice
  v: number; // value
  tpsl: TpSlItem[]; // takeProfitStopLoss orders
  pv: number; // pnlValue
  pp: number; // pnlPercent
  od: string; // openDate (ISO string)
  ost?: OutcomeStatus; // outcome status — pending backend rollout
  iw?: boolean; // isWin — pending backend rollout, see BACKEND_TODO.md #2
}

export interface ActiveOrderRawDto {
  oi: string; // orderId
  ei: string; // eventId
  eoi: string; // eventOutcomeId
  ai: string; // assetId
  et: string; // eventTitle
  eic: string; // eventIcon
  es: string; // eventSlug
  eim: string; // eventImage
  on: string; // outcomeName
  tn: string; // tokenName
  ip: boolean; // isPrimary
  sc: number; // sharesCount
  sp: number; // sharePrice
  si: OrderSide; // side
  fq: number; // filledQuantity
  oq: number; // orderQuantity
  cp: number; // currentPrice
  od: string; // openDate (ISO string)
  st: string; // status
}

export interface PositionHistoryRawDto {
  oi: string; // orderId
  ti: string; // tradeId
  ei: string; // eventId
  eoi: string; // eventOutcomeId
  ai: string; // assetId
  et: string; // eventTitle
  eic: string; // eventIcon
  es: string; // eventSlug
  eim: string; // eventImage
  on: string; // outcomeName
  tn: string; // tokenName
  ip: boolean; // isPrimary
  sc: number; // sharesCount
  si: OrderSide; // side
  ep: number; // executionPrice
  v: number; // value
  f: number; // fees
  ot: string; // orderType
  ext: string; // executionTime (ISO string)
  cp: number; // currentPrice
  pv: number; // pnlValue
  pp: number; // pnlPercent
  ost?: OutcomeStatus; // outcome status — pending backend rollout
  iw?: boolean; // isWin — pending backend rollout, see BACKEND_TODO.md #2
  aep?: number; // avgEntryPrice (Sell rows) — pending backend rollout, see BACKEND_TODO.md #1
  c?: number; // cost (Settlement rows) — pending backend rollout, see BACKEND_TODO.md #3
}

// Paginated response from deals endpoints (mapped from raw)
export interface DealsPageResponse<T> {
  items: T[];
  page: number;
  size: number;
  totalPages: number;
  totalItems?: number;
}

// --- Params ---

export interface DealsOpenPositionsParams {
  page?: number;
  size?: number;
  isPrimary?: boolean;
  search?: string;
  valueFrom?: number;
  valueTo?: number;
  pnlSign?: PnlSign;
  pnlPercentFrom?: number;
  pnlPercentTo?: number;
  lang?: string;
}

export interface DealsActiveOrdersParams {
  page?: number;
  size?: number;
  id?: string;
  isPrimary?: boolean;
  side?: 'BUY' | 'SELL';
  openDatePreset?: DatePreset;
  openDateFrom?: string; // ISO datetime
  openDateTo?: string; // ISO datetime
  search?: string;
  lang?: string;
}

export interface DealsPositionHistoryParams {
  page?: number;
  size?: number;
  isPrimary?: boolean;
  side?: 'BUY' | 'SELL';
  executionDatePreset?: DatePreset;
  executionDateFrom?: string;
  executionDateTo?: string;
  search?: string;
  valueFrom?: number;
  valueTo?: number;
  pnlSign?: PnlSign;
  pnlPercentFrom?: number;
  pnlPercentTo?: number;
  lang?: string;
}

// --- Response Items (mapped model types) ---

export interface DealsOpenPositionItem {
  orderId: string;
  eventId: string;
  eventOutcomeId: string;
  assetId: string;
  eventTitle: string;
  eventIcon: string;
  eventSlug: string;
  eventImage: string;
  outcomeName: string;
  tokenName: string;
  isPrimary: boolean;
  sharesCount: number;
  availableSharesCount: number;
  sharePrice: number;
  cost: number;
  currentPrice: number;
  value: number;
  tpsl: TpSlItem[];
  pnlValue: number;
  pnlPercent: number;
  openDate: string; // ISO string
  outcomeStatus?: OutcomeStatus;
  isWin?: boolean;
}

export interface DealsActiveOrderItem {
  orderId: string;
  eventId: string;
  eventOutcomeId: string;
  assetId: string;
  eventTitle: string;
  eventIcon: string;
  eventSlug: string;
  eventImage: string;
  outcomeName: string;
  tokenName: string;
  isPrimary: boolean;
  sharesCount: number;
  sharePrice: number;
  side: OrderSide;
  filledQuantity: number;
  orderQuantity: number;
  currentPrice: number;
  openDate: string; // ISO string
  status: string;
}

export interface DealsPositionHistoryItem {
  orderId: string;
  tradeId: string;
  eventId: string;
  eventOutcomeId: string;
  assetId: string;
  eventTitle: string;
  eventIcon: string;
  eventSlug: string;
  eventImage: string;
  outcomeName: string;
  tokenName: string;
  isPrimary: boolean;
  sharesCount: number;
  side: OrderSide;
  executionPrice: number;
  value: number;
  fees: number;
  orderType: string;
  executionTime: string;
  currentPrice: number;
  pnlValue: number;
  pnlPercent: number;
  outcomeStatus?: OutcomeStatus;
  isWin?: boolean;
  avgEntryPrice?: number; // Sell rows only — real entry price, see BACKEND_TODO.md #1
  cost?: number; // Settlement rows only — original stake, see BACKEND_TODO.md #3
}

// --- Mapper functions ---

export function mapRawPageResponse<T, R>(
  raw: DealsRawPageResponse<T>,
  mapItem: (item: T) => R,
): DealsPageResponse<R> {
  return {
    items: raw.i.map(mapItem),
    page: raw.p,
    size: raw.s,
    totalPages: raw.tp,
    totalItems: raw.ti,
  };
}

export function mapOpenPosition(dto: OpenPositionRawDto): DealsOpenPositionItem {
  return {
    orderId: dto.oi,
    eventId: dto.ei,
    eventOutcomeId: dto.eoi,
    assetId: dto.ai,
    eventTitle: dto.et,
    eventIcon: dto.eic,
    eventSlug: dto.es,
    eventImage: dto.eim,
    outcomeName: dto.on,
    tokenName: dto.tn,
    isPrimary: dto.ip,
    sharesCount: dto.tsc,
    availableSharesCount: dto.asc,
    sharePrice: dto.sp,
    cost: dto.c,
    currentPrice: dto.cp,
    value: dto.v,
    tpsl: dto.tpsl ?? [],
    pnlValue: dto.pv,
    pnlPercent: dto.pp,
    openDate: dto.od,
    outcomeStatus: dto.ost,
    isWin: dto.iw ?? undefined,
  };
}

export function mapActiveOrder(dto: ActiveOrderRawDto): DealsActiveOrderItem {
  return {
    orderId: dto.oi,
    eventId: dto.ei,
    eventOutcomeId: dto.eoi,
    assetId: dto.ai,
    eventTitle: dto.et,
    eventIcon: dto.eic,
    eventSlug: dto.es,
    eventImage: dto.eim,
    outcomeName: dto.on,
    tokenName: dto.tn,
    isPrimary: dto.ip,
    sharesCount: dto.sc,
    sharePrice: dto.sp,
    side: dto.si,
    filledQuantity: dto.fq,
    orderQuantity: dto.oq,
    currentPrice: dto.cp,
    openDate: dto.od,
    status: dto.st,
  };
}

export function mapPositionHistory(dto: PositionHistoryRawDto): DealsPositionHistoryItem {
  return {
    orderId: dto.oi,
    tradeId: dto.ti,
    eventId: dto.ei,
    eventOutcomeId: dto.eoi,
    assetId: dto.ai,
    eventTitle: dto.et,
    eventIcon: dto.eic,
    eventSlug: dto.es,
    eventImage: dto.eim,
    outcomeName: dto.on,
    tokenName: dto.tn,
    isPrimary: dto.ip,
    sharesCount: dto.sc,
    side: dto.si,
    executionPrice: dto.ep,
    value: dto.v,
    fees: dto.f,
    orderType: dto.ot,
    executionTime: dto.ext,
    currentPrice: dto.cp,
    pnlValue: dto.pv,
    pnlPercent: dto.pp,
    outcomeStatus: dto.ost,
    isWin: dto.iw ?? undefined,
    avgEntryPrice: dto.aep,
    cost: dto.c,
  };
}
